import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Driver } from "../@types";
import { useGetDriversMutation } from "../driverApiSlice";

interface DriverDropdownMenuProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  error?: string;
}

interface DriverOption {
  value: string;
  label: string;
}

const DriverDropdownMenu: React.FC<DriverDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Driver",
  name = "partner",
  id = "partner",
  showLabel = true,
  error,
}) => {
  const [
    getPartners,
    { data: partnerData, isLoading, isError, error: errorData },
  ] = useGetDriversMutation();

  const partners = useMemo(() => {
    return partnerData?.data.data || [];
  }, [partnerData?.data.data]);

  useEffect(() => {
    getPartners({
      pageNumber: 0,
      pageSize: 100,
      assigned: true,
    });
  }, [getPartners]);

  const driversOptions: DriverOption[] = useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading drivers" }];
    }

    return partners.map((vehicle: Driver) => ({
      value: vehicle.id,
      label: vehicle.fullName,
    }));
  }, [partners, isLoading, isError]);

  const handleValueChange = (option: DriverOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: DriverOption) => option.value;
  const getName = (option: DriverOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<DriverOption>
        name={name}
        id={id}
        label={showLabel ? "Driver" : ""}
        options={driversOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading partners"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default DriverDropdownMenu;
